/**
 * @fileoverview User settings budget usage and “request more budget” flow (US-11.4).
 *
 * @purpose Surface spend vs limit on the profile tab and POST to `/api/user/budget-request` with an optional message.
 * @usage Rendered from `UserProfile` on the profile tab when budget fields are present from the server loader.
 * @ownership Korvo core profile UI
 * @dependencies
 * - `../shared/Toast/ToastProvider` (`useToastContext`)
 * @restrictions
 * - Must run inside `ToastProvider` (same tree as `UserProfile`).
 *
 * @docs docs/COOKIE_CONSENT.md (unrelated); API: `src/app/api/user/budget-request/route.ts`
 */

'use client'

import { useCallback, useState } from 'react'

import { useToastContext } from '../shared/Toast/ToastProvider'

export type UserBudgetPanelProps = {
  budgetUsedCents: number
  budgetLimitCents: number | null | undefined
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100)
}

export function UserBudgetPanel({ budgetUsedCents, budgetLimitCents }: UserBudgetPanelProps) {
  const { toast } = useToastContext()
  const [expanded, setExpanded] = useState(false)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const limitLabel =
    budgetLimitCents !== null && budgetLimitCents !== undefined ? formatUsd(budgetLimitCents) : 'Not set'

  const onSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      const res = await fetch('/api/user/budget-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim() === '' ? undefined : message.trim() }),
      })
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(`Budget request failed: ${res.status} ${body.slice(0, 120)}`)
      }
      toast({
        title: 'Request submitted',
        description: 'An administrator will review your budget request.',
        variant: 'success',
      })
      setMessage('')
      setExpanded(false)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      toast({ title: 'Request failed', description: msg, variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }, [message, toast])

  return (
    <section
      className="rounded-lg border border-border-default bg-background-secondary p-4"
      aria-labelledby="user-budget-heading"
    >
      <h3 id="user-budget-heading" className="text-base font-medium text-foreground-default">
        Usage budget
      </h3>
      <p className="mt-1 text-sm text-foreground-muted">
        Estimated spend this period: <span className="font-medium text-foreground-default">{formatUsd(budgetUsedCents)}</span>
        {' · '}
        Limit: <span className="font-medium text-foreground-default">{limitLabel}</span>
      </p>
      <div className="mt-3">
        <button
          type="button"
          data-testid="user-budget-request-more"
          className="rounded-lg border border-border-default bg-background-default px-3 py-2 text-sm font-medium text-foreground-default hover:bg-background-muted"
          onClick={() => setExpanded((v) => !v)}
        >
          Request more budget
        </button>
      </div>
      {expanded ? (
        <div className="mt-4 space-y-3 border-t border-border-default pt-4">
          <label htmlFor="budget-request-message" className="block text-sm font-medium text-foreground-default">
            Message <span className="text-foreground-muted">(optional)</span>
          </label>
          <textarea
            id="budget-request-message"
            name="message"
            value={message}
            onChange={(ev) => setMessage(ev.target.value)}
            rows={4}
            placeholder="Describe why you need a higher limit"
            className="w-full rounded-md border border-border-default bg-background-default px-3 py-2 text-sm text-foreground-default"
          />
          <button
            type="button"
            data-testid="user-budget-submit-request"
            className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            disabled={submitting}
            onClick={() => {
              void onSubmit()
            }}
          >
            Submit request
          </button>
        </div>
      ) : null}
    </section>
  )
}
